import { type FC, useEffect, useCallback, memo, useRef, type PropsWithChildren, useState, useMemo } from "react";
import { FullPageLoader } from "../components/FullPageLoader";
import { FullPageError } from "../components/FullPageError";
import { StyleEditor } from "@mapka/style-editor";
import { ShareMapModal } from "../routes/app/map/components/ShareMapModal";
import { useTRPC } from "../trpc";
import { usePathAccountName, useBasePath } from "../hooks/router";
import { useIntl } from "react-intl";
import { useSetUser, useUser, useSessionAccountName } from "../hooks/user";
import { useExtraPartialSpec, useSetExtraSpec, useStyleEditorView, useSetStyleEditorView } from "../hooks/style";
import { useLocale, useSetLocale } from "../hooks/locale";
import { captureEvent, useFeatureFlag } from "../utils/posthog";
import { useDefaultApiKey, useSetDefaultApiKey } from "../hooks/apiKey";
import { useMutation, useQuery } from "@tanstack/react-query";
import { mapDashboardLayoutRoute } from "../routes/app/map/routes";
import { useAboveFreeModal } from "../hooks/payment";
import { get } from "es-toolkit/compat";
import { useNavigateUserLayout } from "../routes/auth/hooks/navigate";
import { useNavigate } from "@tanstack/react-router";
import type { StyleAndVersions, NewStyle, View, StyleAndVersion } from "@mapka/style-editor";
import type { Locale } from "@project/common";
import type { Style } from "../routes/app/map/types/map";
import type { MapkaStyleSpecification } from "@mapka/maplibre-gl-sdk";

export const MapDashboardLayout: FC<PropsWithChildren> = memo(({ children }) => {
  const user = useUser();
  const setUser = useSetUser();
  const editorView = useStyleEditorView();
  const setStyleEditorView = useSetStyleEditorView();
  const setLocale = useSetLocale();
  const setApiKey = useSetDefaultApiKey();
  const extraSpec = useExtraPartialSpec();
  const setExtraSpec = useSetExtraSpec();
  const styleRef = useRef<Style | undefined>(undefined);
  const intl = useIntl();
  const trpc = useTRPC();
  const locale = useLocale();
  const apiKey = useDefaultApiKey();
  const accountName = usePathAccountName();
  const currentAccountName = useSessionAccountName();
  const pathname = useBasePath();
  const navigate = useNavigateUserLayout();
  const routerNavigate = useNavigate();
  const modal = useAboveFreeModal();
  const [sharing, setSharing] = useState<StyleAndVersion | null>(null);

  const { chatId } = mapDashboardLayoutRoute.useSearch();
  const { map: styleKey } = mapDashboardLayoutRoute.useParams();

  const {
    data: userData,
    isLoading,
    isError: isUserError,
    error: userError,
  } = useQuery(trpc.getCurrentUser.queryOptions(undefined, {
    enabled: !user,
  }));

  const { data: apiKeyData } = useQuery(trpc.getDefaultApiKey.queryOptions(undefined, {
    enabled: Boolean(user),
    refetchInterval: 1000 * 60 * 20,
    refetchOnMount: false,
  }));
  const {
    data: styleAndVersions,
    isLoading: isLoadingStyle,
    isError: isErrorStyle,
    error: styleError,
  } = useQuery(trpc.getStyleWithVersions.queryOptions({
    key: styleKey,
    styleAccountName: accountName,
  }, {
    enabled: Boolean(user),
  }));

  const { mutateAsync: createNewStyleVersion } = useMutation(trpc.createNewStyleVersion.mutationOptions());
  const { mutateAsync: publishStyleVersion } = useMutation(trpc.publishStyleVersion.mutationOptions());
  const { mutateAsync: createNewStyle } = useMutation(trpc.createNewStyle.mutationOptions());
  const { mutateAsync: createUserActivity } = useMutation(trpc.createUserActivity.mutationOptions());

  if (styleAndVersions?.style) {
    styleRef.current = styleAndVersions.style;
  }

  useEffect(() => {
    if (!styleAndVersions) return;

    createUserActivity({
      type: "map",
      objectId: styleAndVersions.style.id,
    }).catch((error: Error) => {
      logger.error({ error }, "Failed to create style user activity");
    });
  }, [get(styleAndVersions, "style.id")]);

  useEffect(() => {
    if (userData) {
      if (userData.locale !== intl.locale) {
        setLocale(userData.locale as Locale);
      }
    }
  }, [userData, intl, setLocale]);

  const onViewChange = useCallback((view: View) => {
    setStyleEditorView(view);
    void routerNavigate({
      to: `/$accountName/maps/$map/${view}`,
      params: { map: styleKey, accountName },
    });
  }, [routerNavigate, styleKey, accountName, setStyleEditorView]);

  const onNavigateBack = useCallback(() => {
    void routerNavigate({
      to: "/$accountName",
      params: { accountName: currentAccountName ?? accountName },
    });
  }, [routerNavigate, currentAccountName, accountName]);

  const onLoad = useCallback(() => {
    setExtraSpec(undefined);
  }, [extraSpec]);

  const onCreateNewStyle = useCallback(async (newStyle: NewStyle): Promise<StyleAndVersions> => {
    return (await createNewStyle(newStyle)) as unknown as StyleAndVersions;
  }, [createNewStyle]);

  const onSaveStyle = useCallback(async (updatedStyle: MapkaStyleSpecification): Promise<StyleAndVersions> => {
    if (!styleRef.current?.id) throw new Error("Style not found");

    return (await createNewStyleVersion({
      styleId: styleRef.current.id,
      style: updatedStyle,
    })) as unknown as StyleAndVersions;
  }, []);

  const onPublishStyle = useCallback(async (versionId: number): Promise<StyleAndVersions> => {
    if (!styleRef.current?.id) throw new Error("Style not found");

    return (await publishStyleVersion({
      styleId: styleRef.current.id,
      versionId: versionId,
    })) as unknown as StyleAndVersions;
  }, []);

  const onChatChange = useCallback((newChatId: number) => {
    void routerNavigate({
      to: `/$accountName/maps/$map/${editorView}`,
      params: { map: styleKey, accountName },
      search: { chatId: newChatId },
    });
  }, [routerNavigate, editorView, styleKey, accountName]);

  const handleShare = useCallback((style: StyleAndVersion) => {
    setSharing(style);
  }, []);

  useEffect(() => {
    if (userData) {
      setUser(userData);
      navigate(userData);
    }
  }, [userData, setUser, navigate, pathname]);

  useEffect(() => {
    if (apiKeyData) {
      setApiKey(apiKeyData);
    }
  }, [apiKeyData]);

  const enableAISettings = useFeatureFlag("enable-style-editor-ai-settings");
  const featureFlags = useMemo(() => ({ enableAISettings }), [enableAISettings]);

  if (isLoading || isLoadingStyle) {
    return <FullPageLoader />;
  }
  if (isUserError) {
    return <FullPageError error={userError} />;
  }
  if (isErrorStyle) {
    return <FullPageError error={styleError} />;
  }
  if (!user || !styleAndVersions || !apiKey) {
    return <FullPageLoader />;
  }

  const style = styleAndVersions as unknown as StyleAndVersions;
  const extraSpecCast = extraSpec as unknown as Partial<MapkaStyleSpecification>;

  return (
    <>
      {children}
      <StyleEditor
        style={style}
        extraSpec={extraSpecCast}
        mapkaApiKey={apiKey}
        mapkaAccountName={currentAccountName}
        view={editorView}
        locale={locale}
        chatId={chatId}
        featureFlags={featureFlags}
        onLoad={onLoad}
        onSaveStyle={onSaveStyle}
        onPublishStyle={onPublishStyle}
        onCreateStyle={onCreateNewStyle}
        captureEvent={captureEvent}
        onViewChange={onViewChange}
        onChatChange={onChatChange}
        onNavigateBack={onNavigateBack}
        onShare={handleShare}
      />
      {sharing && (
        <ShareMapModal
          opened
          onClose={() => { setSharing(null); }}
          style={sharing.style}
          version={sharing.version}
          accountName={accountName}
        />
      )}
      {modal}
    </>
  );
});
